import { useState, useEffect, useMemo } from "react";
import Swal from "sweetalert2";
import "bootstrap/dist/css/bootstrap.min.css";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPencil,
  faPlus,
  faTrash,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import { API_URL } from "../../config";
import LoadingSpinner from "../../components/LoadingSpinner";
import { PermissionWrapper, hasPermission } from "../../helpers/permission.js";

const EmailMapping = () => {
  const [emailMapping, setEmailMapping] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterLoading, setFilterLoading] = useState(false);
  const [error, setError] = useState(null);
  const token = localStorage.getItem("authToken");

  // 🔎 Search Filters
  const [locationFilter, setLocationFilter] = useState("");
  const [emailFilter, setEmailFilter] = useState("");

  // 📑 Pagination
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 100;

  const fetchEmailConfig = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}settings/emailMapping/list`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      setEmailMapping(response.data.data || []); // Adjust this path if the API structure differs
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmailConfig();
  }, []);

  // 🔎 Apply filters
  const filteredMappings = useMemo(() => {
    return emailMapping.filter((mapping) => {
      if (
        locationFilter &&
        !(mapping.locationName || "")
          .toLowerCase()
          .includes(locationFilter.toLowerCase())
      ) {
        return false;
      }
      if (
        emailFilter &&
        !(mapping.emailFrom || "")
          .toLowerCase()
          .includes(emailFilter.toLowerCase())
      ) {
        return false;
      }
      return true;
    });
  }, [emailMapping, locationFilter, emailFilter]);

  useEffect(() => {
    if (!emailMapping.length) return;

    setFilterLoading(true);
    const timer = setTimeout(() => {
      setFilterLoading(false);
    }, 300);

    return () => clearTimeout(timer);
  }, [locationFilter, emailFilter, emailMapping]);

  // 📑 Update pagination when filters change
  useEffect(() => {
    setTotal(filteredMappings.length);
    setPageCount(Math.ceil(filteredMappings.length / limit));
    setPage(1);
  }, [filteredMappings, limit]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pageCount) {
      setPage(newPage);
    }
  };

  const start = (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);
  const paginatedMappings = filteredMappings.slice(start - 1, end);

  // Delete Confirmation
  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You will not be able to recover this record!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonClass: "btn-danger",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel!",
    });

    if (result.isConfirmed) {
      try {
        const response = await axios.delete(
          `${API_URL}settings/emailMapping/${id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        // Remove the deleted item from state
        setEmailMapping((prev) => prev.filter((mapping) => mapping.id !== id));

        // Show message from server
        Swal.fire("Deleted!", "Your record has been deleted.", "success");
      } catch (error) {
        Swal.fire(
          "Error",
          error.response?.data?.message ||
          "Something went wrong while deleting.",
          "error"
        );
      }
    }
  };

  useEffect(() => {
    const handleClearFilters = () => {
      setLocationFilter("");
      setEmailFilter("");
    };
    window.addEventListener('clearEmailMappingFilterEvent', handleClearFilters);

    return () => {
      window.removeEventListener('clearEmailMappingFilterEvent', handleClearFilters);
    };
  }, []);


  return (
    <div className="mt-5">
      <div className="row pb-2">
        <div className="col-md-6">
          <h4 className="panel-title pull-left">Manage Email Mapping</h4>
        </div>
        <PermissionWrapper module="settings_mapping" action="add">
          <div className="col-md-6 text-right">
            <Link
              to="/Settings/EmailMapping/add"
              className="btn btn-default btn-sm"
            >
              <FontAwesomeIcon icon={faPlus} /> Add Email
            </Link>
          </div>
        </PermissionWrapper>
      </div>

      <div className="panel panel-default with-rounded-corner with-shadow">
        {/* 📑 Pagination Controls */}
        {emailMapping.length > 0 && (
          <div className="d-flex align-items-center justify-content-between p-3 border-bottom">
            <div className="panel-heading border-0">
              <h4 className="panel-title m-0">Email Mappings</h4>
            </div>
            <div className="d-inline-block">
              <span style={{ fontSize: "0.8rem" }} className="me-2">
                {start} to {end} of {total}
              </span>
              <button
                className="btn btn-default btn-sm"
                title="Previous"
                onClick={() => handlePageChange(page - 1)}
                disabled={page === 1}
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>
              &nbsp;
              <button
                className="btn btn-default btn-sm"
                title="Next"
                onClick={() => handlePageChange(page + 1)}
                disabled={page === pageCount}
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
            </div>
          </div>
        )}

        {loading || emailMapping.length > 0 ? (
          <div className="table-responsive">
            <table className="table m-b-0">
              <thead>
                <tr>
                  <th style={{ textAlign: "center" }}>#</th>
                  <th>Location Name</th>
                  <th>Email Id</th>
                  <th style={{ width: "50px" }}>Action</th>
                </tr>
                {/* 🔎 Search Filters Row */}
                <tr>
                  <th></th>
                  <th>
                    <div
                      className="input-group input-group-sm"
                      style={{ width: "180px" }}
                    >
                      <input
                        type="text"
                        className="form-control input-sm"
                        placeholder="Search location..."
                        style={{
                          width: "100%",
                          fontSize: "12px",
                          padding: "3px 6px",
                          height: "28px",
                        }}
                        value={locationFilter}
                        onChange={(e) => setLocationFilter(e.target.value)}
                      />
                    </div>
                  </th>
                  <th>
                    <div
                      className="input-group input-group-sm"
                      style={{ width: "180px" }}
                    >
                      <input
                        type="text"
                        className="form-control input-sm"
                        placeholder="Search email..."
                        style={{
                          width: "100%",
                          fontSize: "12px",
                          padding: "3px 6px",
                          height: "28px",
                        }}
                        value={emailFilter}
                        onChange={(e) => setEmailFilter(e.target.value)}
                      />
                    </div>
                  </th>
                  <th>
                    <button
                      className="btn btn-default btn-sm"
                      onClick={() => {
                        setLocationFilter("");
                        setEmailFilter("");
                      }}
                    >
                      Clear
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading || filterLoading ? (
                  Array.from({ length: 10 }).map((_, rowIndex) => (
                    <tr key={rowIndex} className="skeleton-row">
                      {Array.from({ length: 4 }).map((_, colIndex) => (
                        <td key={colIndex}>
                          <div className="skeleton-box"></div>
                        </td>
                      ))}
                    </tr>
                  ))
                ) : paginatedMappings.length > 0 ? (
                  paginatedMappings.map((post, index) => (
                    <tr key={post.id}>
                      <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                        {(page - 1) * limit + index + 1}
                      </td>
                      <td>{post.locationName}</td>
                      <td>{post.emailFrom}</td>
                      <td style={{ width: "50px" }}>
                        <div className="d-flex gap-2">
                          <PermissionWrapper module="settings_mapping" action="edit">
                            <Link
                              to={`/Settings/EmailMapping/edit/${post.id}`}
                              className="btn btn-warning btn-xs"
                              title="Edit Email Mapping"
                            >
                              <FontAwesomeIcon icon={faPencil} />
                            </Link>
                          </PermissionWrapper>
                          <PermissionWrapper module="settings_mapping" action="delete">
                            <button
                              className="cancel btn btn-danger btn-xs"
                              onClick={() => handleDelete(post.id)}
                              title="Delete"
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          </PermissionWrapper>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" style={{ textAlign: "center" }}>
                      No Records Found
                    </td>
                  </tr>
                )}
              </tbody>

            </table>
          </div>
        ) : (
          <div className="alert alert-warning m-3">
            No records found.{" "}
            <Link to="/settings/emailmapping/add">Add Email Mapping</Link>.
          </div>
        )}
      </div>

      <Link to="#" data-click="scroll-top" className="btn-scroll-top fade">
        <i className="ti-arrow-up"></i>
      </Link>
    </div>
  );
};

export default EmailMapping;
